import React, { useMemo, useState } from "react";
import { cn } from "../../../../lib/utils";
import {
  useDeletePoolItemMutation,
  useGetPoolPage,
  useUpdatePoolItemMutation,
} from "../../api/react-query";
import {
  AppDialog,
  Badge,
  Button,
  DataTableCard,
  DataTableCell,
  DataTableRow,
  FormField,
  Input,
  LoadingBlock,
  Select,
} from "../../../../shared/ui/composites";
import { formatDateTime } from "../../../../shared/utils/numberLocale";
import { fmtInt } from "../../../../shared/utils/formatters";

const STATUS_META = {
  pending: { label: "در انتظار", variant: "warning" },
  sending: { label: "در حال ارسال", variant: "default" },
  sent: { label: "ارسال شد", variant: "job" },
  failed: { label: "ناموفق", variant: "destructive" },
  cancelled: { label: "لغو شده", variant: "muted" },
};

const CHANNEL_META = {
  email: { label: "ایمیل", className: "bg-sky-50 text-sky-700" },
  sms: { label: "پیامک", className: "bg-violet-50 text-violet-700" },
};

const COLUMNS = [
  { key: "id", label: "#" },
  { key: "status", label: "وضعیت" },
  { key: "channel", label: "کانال" },
  { key: "recipient", label: "گیرنده" },
  { key: "subject", label: "موضوع / متن" },
  { key: "provider", label: "ارائه‌دهنده" },
  { key: "attempts", label: "تلاش" },
  { key: "scheduled", label: "زمان" },
  { key: "actions", label: "عملیات" },
];

const PAGE_SIZE = 20;

function formatDate(value) {
  if (!value) return "—";
  try {
    return formatDateTime(value);
  } catch (_) {
    return String(value);
  }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", variant: "muted" };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function ChannelBadge({ channel }) {
  const meta = CHANNEL_META[channel] || {
    label: channel || "—",
    className: "bg-slate-100 text-slate-600",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-semibold",
        meta.className,
      )}
    >
      {meta.label}
    </span>
  );
}

export default function PoolPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [previewRow, setPreviewRow] = useState(null);

  const filters = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      status: status || undefined,
      channel: channel || undefined,
      search: search || undefined,
    }),
    [page, status, channel, search],
  );

  const { data, isLoading, isFetching, refetch } = useGetPoolPage(filters);
  const updateMutation = useUpdatePoolItemMutation();
  const deleteMutation = useDeletePoolItemMutation();

  const rows = data?.data || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const cancelItem = async (id) => {
    if (!window.confirm("لغو این اعلان؟")) return;
    try {
      await updateMutation.mutateAsync({
        id,
        body: { status: "cancelled" },
      });
    } catch (err) {
      console.error(err);
      window.alert(err?.response?.data?.error || err.message || "خطا");
    }
  };

  const retryItem = async (id) => {
    try {
      await updateMutation.mutateAsync({
        id,
        body: { status: "pending", error_message: null },
      });
    } catch (err) {
      console.error(err);
      window.alert(err?.response?.data?.error || err.message || "خطا");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("حذف دائمی این اعلان از صف؟")) return;
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      console.error(err);
      window.alert(err?.response?.data?.error || err.message || "خطا");
    }
  };

  return (
    <div className="space-y-4 p-4 md:p-6" dir="rtl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">صف اعلان‌ها</h1>
          <p className="mt-1 text-sm text-slate-500">
            صف ارسال ایمیل و پیامک — وضعیت، تلاش‌ها و زمان‌بندی
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          بروزرسانی
        </Button>
      </div>

      <form
        onSubmit={applySearch}
        className="grid grid-cols-12 items-end gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
      >
        <FormField
          label="جستجو"
          htmlFor="pool-search"
          layout="inline"
          className="col-span-6 min-w-0"
        >
          <Input
            id="pool-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="نام، آدرس، موضوع..."
            className="w-full"
          />
        </FormField>
        <FormField
          label="وضعیت"
          htmlFor="pool-status"
          layout="inline"
          className="col-span-2 min-w-0"
        >
          <Select
            id="pool-status"
            variant="filter"
            className="w-full"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">همه</option>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <option key={key} value={key}>
                {meta.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField
          label="کانال"
          htmlFor="pool-channel"
          layout="inline"
          className="col-span-2 min-w-0"
        >
          <Select
            id="pool-channel"
            variant="filter"
            className="w-full"
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
              setPage(1);
            }}
          >
            <option value="">همه</option>
            <option value="email">ایمیل</option>
            <option value="sms">پیامک</option>
          </Select>
        </FormField>
        <FormField label="&nbsp;" layout="inline" className="col-span-2 min-w-0">
          <Button type="submit" variant="submit" className="w-full">
            فیلتر
          </Button>
        </FormField>
      </form>

      {isLoading ? (
        <LoadingBlock message="در حال بارگذاری صف اعلان‌ها..." />
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-white shadow-card-sm">
          <DataTableCard
            className="border-0 shadow-none rounded-none"
            columns={COLUMNS}
            headerClassName="bg-gradient-to-br from-slate-50 to-muted"
            isEmpty={rows.length === 0}
            emptyMessage="موردی در صف نیست"
            minWidth="960px"
          >
            {rows.map((row) => (
              <DataTableRow key={row.id}>
                <DataTableCell className="!text-xs text-slate-400">{fmtInt(row.id)}</DataTableCell>
                <DataTableCell>
                  <div className="flex flex-col gap-1">
                    <StatusBadge status={row.status} />
                    {row.error_message ? (
                      <span
                        className="max-w-[160px] truncate text-xs text-rose-500"
                        title={row.error_message}
                      >
                        {row.error_message}
                      </span>
                    ) : null}
                  </div>
                </DataTableCell>
                <DataTableCell>
                  <ChannelBadge channel={row.channel} />
                </DataTableCell>
                <DataTableCell>
                  <div className="flex flex-col">
                    <span className="font-semibold text-slate-800">
                      {row.recipient_name || "—"}
                    </span>
                    <span className="text-xs text-slate-500" dir="ltr">
                      {row.recipient_address}
                    </span>
                  </div>
                </DataTableCell>
                <DataTableCell>
                  <button
                    type="button"
                    className="max-w-[260px] text-right hover:opacity-80"
                    onClick={() => setPreviewRow(row)}
                    title="مشاهده متن کامل"
                  >
                    {row.subject ? (
                      <div className="truncate text-sm font-medium text-slate-800">
                        {row.subject}
                      </div>
                    ) : null}
                    <div className="truncate text-xs text-slate-500 underline-offset-2 hover:underline" title={row.body}  >
                      {row.body}
                    </div>
                  </button>
                </DataTableCell>
                <DataTableCell className="text-xs text-slate-600">
                  {row.provider_id || "—"}
                </DataTableCell>
                <DataTableCell className="text-xs tabular-nums text-slate-600">
                  {fmtInt(row.attempts)}/{fmtInt(row.max_attempts)}
                </DataTableCell>
                <DataTableCell>
                  <div className="flex flex-col text-xs  leading-tight  text-slate-500">
                    <span title="زمان‌بندی">{formatDate(row.scheduled_at)}</span>
                    {row.sent_at ? (
                      <span className="text-emerald-600" title="ارسال">
                        {formatDate(row.sent_at)}
                      </span>
                    ) : null}
                  </div>
                </DataTableCell>
                <DataTableCell>
                  <div className="flex flex-wrap gap-1">
                    {row.status === "pending" || row.status === "failed" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-amber-700 hover:bg-amber-50"
                        onClick={() => cancelItem(row.id)}
                      >
                        لغو
                      </Button>
                    ) : null}
                    {row.status === "failed" || row.status === "cancelled" ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => retryItem(row.id)}
                      >
                        تلاش مجدد
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteItem(row.id)}
                      disabled={deleteMutation.isPending}
                    >
                      حذف
                    </Button>
                  </div>
                </DataTableCell>
              </DataTableRow>
            ))}
          </DataTableCard>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
            <span>
              {total
                ? `نمایش ${fmtInt((page - 1) * PAGE_SIZE + 1)}–${fmtInt(Math.min(page * PAGE_SIZE, total))} از ${fmtInt(total)}`
                : "۰ مورد"}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  قبلی
                </Button>
                <span className="px-2 tabular-nums">
                  {fmtInt(page)} / {fmtInt(totalPages)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  بعدی
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <AppDialog
        open={!!previewRow}
        onOpenChange={(open) => !open && setPreviewRow(null)}
        title={previewRow?.subject || "متن اعلان"}
        size="lg"
        showFooter={false}
      >
        {previewRow ? (
          <div className="space-y-3 text-sm text-slate-700">
            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
              <ChannelBadge channel={previewRow.channel} />
              <StatusBadge status={previewRow.status} />
              <span dir="ltr">{previewRow.recipient_address}</span>
            </div>
            {previewRow.subject ? (
              <div>
                <div className="mb-1 text-xs font-semibold text-slate-500">موضوع</div>
                <div className="rounded-lg bg-slate-50 p-3 font-medium text-slate-900">
                  {previewRow.subject}
                </div>
              </div>
            ) : null}
            <div>
              <div className="mb-1 text-xs font-semibold text-slate-500">متن کامل</div>
              <div className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-3 leading-7">
                {previewRow.body || "—"}
              </div>
            </div>
            {previewRow.error_message ? (
              <div className="rounded-lg bg-rose-50 p-3 text-rose-700">
                {previewRow.error_message}
              </div>
            ) : null}
          </div>
        ) : null}
      </AppDialog>
    </div>
  );
}
